import { getBackendUrl } from "@/lib/backend";
import { useCallback, useEffect, useState } from "react";
import axios, { API } from "@/lib/api";
import { formatApiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  BookMarked, Folder, Trash2, Plus, Users2, Share2, Globe, Lock,
} from "lucide-react";
import LuxuryConfirmModal from "@/components/LuxuryConfirmModal";
import PortfolioDetail from "@/components/PortfolioDetail";
import SharePortfolioDialog from "@/components/SharePortfolioDialog";

const BACKEND = getBackendUrl();

function PortfolioCard({ p, onOpen, onDelete, onShare, sharedView }) {
  // Slight visual distinction: portfolios shared INTO this user (i.e.,
  // owned by a teammate) get a subtle "Shared by X" header strip.
  const ownerLabel = sharedView ? `Shared by ${p.owner_name}` : null;
  const sharedToTeam = p.is_shared && !sharedView;
  return (
    <div
      data-testid={`portfolio-card-${p.id}`}
      className="group rounded-2xl border border-white/10 bg-[#0F0F0F] hover:border-[#D4AF37]/35 transition overflow-hidden"
    >
      <button
        type="button"
        onClick={() => onOpen(p)}
        className="block w-full text-left"
      >
        <div className="relative aspect-[4/3] bg-[#0B0B0B] overflow-hidden">
          {p.cover_image_url ? (
            <img
              src={`${BACKEND}${p.cover_image_url}`}
              alt={p.name}
              className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <Folder className="w-10 h-10 text-white/15" />
            </div>
          )}
          <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-black/85 to-transparent" />
          <div className="absolute bottom-2 left-3 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            {p.prefix}-###
          </div>
          {sharedToTeam && (
            <div
              className="absolute top-2 right-2 inline-flex items-center gap-1 text-[9px] uppercase tracking-[0.18em] text-[#D4AF37] bg-black/60 border border-[#D4AF37]/40 rounded-full px-2 py-0.5"
              title={`Shared with ${p.shared_in_workspace_name || "your team"}`}
              data-testid={`portfolio-card-${p.id}-shared-badge`}
            >
              <Users2 className="w-2.5 h-2.5" /> Team
            </div>
          )}
        </div>
        <div className="p-3">
          {ownerLabel && (
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] mb-1" data-testid={`portfolio-card-${p.id}-owner`}>
              {ownerLabel}
              {p.shared_in_workspace_name ? ` · ${p.shared_in_workspace_name}` : ""}
            </div>
          )}
          <div className="font-display text-base text-white truncate">{p.name}</div>
          <div className="text-[11px] text-white/45 mt-0.5">
            {p.item_count} {p.item_count === 1 ? "piece" : "pieces"}
            {p.atelier_name ? ` · ${p.atelier_name}` : ""}
          </div>
        </div>
      </button>
      {!sharedView && (
        <div className="px-3 pb-3 flex items-center justify-between">
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onShare(p); }}
            data-testid={`portfolio-share-${p.id}`}
            className={`text-[10px] inline-flex items-center gap-1 transition ${
              sharedToTeam ? "text-[#D4AF37] hover:text-[#F3CE5A]" : "text-white/40 hover:text-[#D4AF37]"
            }`}
          >
            {sharedToTeam ? <Globe className="w-3 h-3" /> : <Share2 className="w-3 h-3" />}
            {sharedToTeam ? "Shared with team" : "Share"}
          </button>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onDelete(p); }}
            data-testid={`portfolio-delete-${p.id}`}
            className="text-[10px] text-white/40 hover:text-red-400 inline-flex items-center gap-1 transition"
          >
            <Trash2 className="w-3 h-3" /> Delete
          </button>
        </div>
      )}
      {sharedView && (
        <div className="px-3 pb-3 flex items-center justify-end">
          <span className="inline-flex items-center gap-1 text-[10px] text-white/40">
            <Lock className="w-3 h-3" /> Read-only
          </span>
        </div>
      )}
    </div>
  );
}

export default function PortfolioPanel({ inIframe }) {
  const [portfolios, setPortfolios] = useState([]);
  const [shared, setShared] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [openDetail, setOpenDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newPrefix, setNewPrefix] = useState("");
  const [newAtelier, setNewAtelier] = useState("");
  const [newDesc, setNewDesc] = useState("");

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const [mine, sh] = await Promise.all([
        axios.get(`${API}/studio/portfolios`),
        axios.get(`${API}/studio/portfolios/shared`).catch(() => ({ data: { portfolios: [] } })),
      ]);
      setPortfolios(mine.data?.portfolios || []);
      setShared(sh.data?.portfolios || []);
    } catch (err) {
      console.error("loadList portfolios failed", err);
      toast.error("Could not load portfolios.");
    } finally {
      setLoading(false);
    }
  }, []);

  // Browser back-button trap while a portfolio detail is open. Without
  // this, pressing the OS/browser back inside a portfolio takes the
  // user to whatever page they came from before /app (often /catalog
  // since users hop between Studio / Catalog / Portfolio). With this,
  // back closes the detail and returns to the portfolio list — which is
  // what users expect.
  useEffect(() => {
    if (!openId) return;
    // Push a sentinel entry so the first popstate consumes it instead
    // of leaving /app.
    window.history.pushState({ facetra_portfolio_detail: true }, "", window.location.href);
    const onPop = () => {
      setOpenId(null);
      setOpenDetail(null);
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [openId]);

  const loadDetail = useCallback(async (id) => {
    try {
      const r = await axios.get(`${API}/studio/portfolios/${id}`);
      setOpenDetail(r.data);
    } catch (err) {
      console.error("loadDetail portfolio failed", err);
      toast.error("Could not open portfolio.");
      setOpenId(null);
    }
  }, []);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => { if (openId) loadDetail(openId); else setOpenDetail(null); }, [openId, loadDetail]);

  const createPortfolio = async () => {
    if (!newName.trim()) return toast.error("Portfolio name is required.");
    try {
      const r = await axios.post(`${API}/studio/portfolios`, {
        name: newName.trim(),
        prefix: newPrefix.trim() || undefined,
        atelier_name: newAtelier.trim() || undefined,
        description: newDesc.trim() || undefined,
      });
      toast.success(`Created “${r.data.name}”`);
      setNewName(""); setNewPrefix(""); setNewAtelier(""); setNewDesc("");
      setCreating(false);
      await loadList();
    } catch (err) {
      toast.error(formatApiError(err, "Create failed."));
    }
  };

  const confirmDeletePortfolio = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API}/studio/portfolios/${deleteTarget.id}`);
      toast.success("Portfolio deleted");
      setDeleteTarget(null);
      if (openId === deleteTarget.id) setOpenId(null);
      await loadList();
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed."));
    }
  };

  if (openDetail) {
    return (
      <PortfolioDetail
        portfolio={openDetail}
        onBack={() => setOpenId(null)}
        onChanged={() => loadDetail(openDetail.id)}
        inIframe={inIframe}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
            <BookMarked className="w-3 h-3" /> Library
          </div>
          <h2 className="font-display text-2xl md:text-3xl text-white mt-1">Your portfolios</h2>
          <p className="text-[12px] text-white/55 mt-0.5">
            Save renders into collections, auto-numbered with BOMs, exportable as a lookbook ZIP.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setCreating((v) => !v)}
          data-testid="portfolio-create-toggle"
          className="inline-flex items-center gap-1.5 text-xs px-3.5 py-1.5 rounded-full border border-[#D4AF37]/45 text-[#D4AF37] hover:bg-[#1A1814] transition"
        >
          <Plus className="w-3.5 h-3.5" /> New portfolio
        </button>
      </div>

      {creating && (
        <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Name</label>
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                maxLength={80}
                placeholder="e.g. Diwali 2026 Collection"
                data-testid="portfolio-new-name"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Design prefix</label>
              <input
                type="text"
                value={newPrefix}
                onChange={(e) => setNewPrefix(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))}
                maxLength={8}
                placeholder="DWL"
                data-testid="portfolio-new-prefix"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Atelier name</label>
              <input
                type="text"
                value={newAtelier}
                onChange={(e) => setNewAtelier(e.target.value)}
                maxLength={80}
                placeholder="(optional — shown on lookbook cover)"
                data-testid="portfolio-new-atelier"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition"
              />
            </div>
            <div className="md:col-span-2">
              <label className="text-[10px] uppercase tracking-[0.22em] text-white/45 block mb-1">Description</label>
              <textarea
                value={newDesc}
                onChange={(e) => setNewDesc(e.target.value)}
                maxLength={400}
                rows={2}
                placeholder="A short tagline shown on the lookbook cover (optional)."
                data-testid="portfolio-new-desc"
                className="w-full bg-[#141414] border border-white/10 rounded-md px-2.5 py-2 text-[12px] text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition resize-none leading-snug"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setCreating(false)}
              className="text-xs px-4 py-2 rounded-full border border-white/15 text-white/75 hover:bg-white/5 transition"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={createPortfolio}
              disabled={!newName.trim()}
              data-testid="portfolio-new-submit"
              className="text-xs px-4 py-2 rounded-full bg-[#D4AF37] text-black font-medium hover:bg-[#F3CE5A] transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Create
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-10 text-center text-white/55 text-sm">
          Loading portfolios…
        </div>
      ) : portfolios.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-[#0F0F0F] p-12 text-center">
          <BookMarked className="w-7 h-7 text-[#D4AF37]/60 mx-auto mb-2" />
          <p className="text-sm text-white/65">No portfolios yet.</p>
          <p className="text-[11px] text-white/40 mt-1">Create one to start saving your hero renders into a presentable lookbook.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-mine-grid">
          {portfolios.map((p) => (
            <PortfolioCard
              key={p.id}
              p={p}
              onOpen={(x) => setOpenId(x.id)}
              onDelete={setDeleteTarget}
              onShare={setShareTarget}
            />
          ))}
        </div>
      )}

      {/* Shared with me — portfolios from teammates */}
      {shared.length > 0 && (
        <div className="pt-8 space-y-3" data-testid="portfolio-shared-section">
          <div className="border-t border-white/8 pt-6 flex items-baseline justify-between">
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                <Users2 className="w-3 h-3" /> Shared with you
              </div>
              <h3 className="font-display text-xl text-white mt-1">From your team</h3>
            </div>
            <span className="text-[10px] text-white/45 tabular-nums">
              {shared.length} portfolio{shared.length === 1 ? "" : "s"}
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolio-shared-grid">
            {shared.map((p) => (
              <PortfolioCard
                key={p.id}
                p={p}
                onOpen={(x) => setOpenId(x.id)}
                onDelete={() => { /* not allowed */ }}
                onShare={() => { /* not allowed */ }}
                sharedView
              />
            ))}
          </div>
        </div>
      )}

      <LuxuryConfirmModal
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        title="Delete this portfolio?"
        description={`“${deleteTarget?.name || ""}” and ${deleteTarget?.item_count || 0} saved pieces will be removed. The underlying renders stay in your Studio.`}
        confirmLabel="Delete portfolio"
        onConfirm={confirmDeletePortfolio}
        destructive
      />

      <SharePortfolioDialog
        portfolio={shareTarget}
        open={!!shareTarget}
        onOpenChange={(v) => !v && setShareTarget(null)}
        onChanged={loadList}
      />
    </div>
  );
}
